import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../utils/api';
import { Loading } from '../../components/Shared';

const fmtEur = n => Number(n || 0).toLocaleString('fr-FR', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
});
const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

// ── Formulaire opportunité ────────────────────────────────────
function ModalOpportunite({ opp, clients, etapes, canaux, onSave, onClose }) {
  const isEdit = !!opp?.id;
  const [form, setForm] = useState({
    client_id:          opp?.client_id          || '',
    intitule:           opp?.intitule           || '',
    etape_id:           opp?.etape_id           || (etapes[0]?.id || ''),
    montant_ht:         opp?.montant_ht         || '',
    probabilite:        opp?.probabilite        || 10,
    canal_source_id:    opp?.canal_source_id    || '',
    date_closing_prevue:opp?.date_closing_prevue?.slice(0,10) || '',
    motif_perte:        opp?.motif_perte        || '',
    notes:              opp?.notes              || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inputStyle = {
    width: '100%', padding: '0.45rem 0.65rem', borderRadius: '6px',
    border: '1px solid var(--border)', background: 'var(--bg-elevated)',
    color: 'var(--text-primary)', fontSize: '0.88rem', boxSizing: 'border-box',
  };

  async function handleSave() {
    if (!form.client_id || !form.intitule) return setError('Client et intitulé requis');
    setSaving(true); setError('');
    try {
      const body = {
        ...form,
        montant_ht:         form.montant_ht  ? parseFloat(form.montant_ht) : null,
        probabilite:        parseInt(form.probabilite) || 0,
        etape_id:           form.etape_id           || null,
        canal_source_id:    form.canal_source_id    || null,
        date_closing_prevue:form.date_closing_prevue || null,
      };
      if (isEdit) {
        await apiFetch(`/crm/opportunites/${opp.id}`, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        await apiFetch('/crm/opportunites', { method: 'POST', body: JSON.stringify(body) });
      }
      onSave();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ width: 540 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? 'Modifier l\'opportunité' : 'Nouvelle opportunité'}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div className="form-error">{error}</div>}
          <div className="form-grid-2">
            <div style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Client *</label>
              <select style={inputStyle} value={form.client_id}
                onChange={e => set('client_id', e.target.value)}>
                <option value="">Sélectionner…</option>
                {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.nom_client}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Intitulé *</label>
              <input style={inputStyle} value={form.intitule}
                onChange={e => set('intitule', e.target.value)}
                placeholder="Ex : Formation CACES 2025" />
            </div>
            <div>
              <label className="form-label">Étape pipeline</label>
              <select style={inputStyle} value={form.etape_id || ''}
                onChange={e => {
                  const etape = etapes.find(et => String(et.id) === e.target.value);
                  set('etape_id', e.target.value);
                  if (etape) set('probabilite', etape.probabilite_defaut);
                }}>
                {etapes.map(e => <option key={e.id} value={e.id}>{e.libelle}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Probabilité (%)</label>
              <input type="number" min="0" max="100" style={inputStyle}
                value={form.probabilite} onChange={e => set('probabilite', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Montant HT (€)</label>
              <input type="number" min="0" style={inputStyle}
                value={form.montant_ht} onChange={e => set('montant_ht', e.target.value)}
                placeholder="0" />
            </div>
            <div>
              <label className="form-label">Closing prévu</label>
              <input type="date" style={inputStyle}
                value={form.date_closing_prevue}
                onChange={e => set('date_closing_prevue', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Canal source</label>
              <select style={inputStyle} value={form.canal_source_id || ''}
                onChange={e => set('canal_source_id', e.target.value)}>
                <option value="">—</option>
                {canaux.map(c => <option key={c.id} value={c.id}>{c.libelle}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Notes</label>
              <textarea style={{ ...inputStyle, height: 60, resize: 'vertical' }}
                value={form.notes} onChange={e => set('notes', e.target.value)} />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement…' : isEdit ? 'Modifier' : 'Créer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Carte opportunité (kanban) ────────────────────────────────
function OppCard({ opp, etapes, onMove, onEdit, onDelete }) {
  const isOverdue = opp.date_closing_prevue && new Date(opp.date_closing_prevue) < new Date();

  return (
    <div className="crm-opp-card">
      <div className="crm-opp-card-title">{opp.intitule}</div>
      <div className="crm-opp-card-client">{opp.nom_client}</div>
      {opp.montant_ht > 0 && (
        <div className="crm-opp-card-montant">
          {fmtEur(opp.montant_ht)}
          <span className="crm-opp-proba">· {opp.probabilite}%</span>
        </div>
      )}
      {opp.date_closing_prevue && (
        <div className="crm-opp-card-date" style={{ color: isOverdue ? '#f87171' : 'var(--text-tertiary)' }}>
          {isOverdue ? '⚠ ' : '📅 '}{fmtDate(opp.date_closing_prevue)}
        </div>
      )}
      {opp.commercial_nom && (
        <div className="crm-opp-card-commercial">{opp.commercial_nom}</div>
      )}
      <div className="crm-opp-card-actions">
        {/* Déplacer vers étape précédente / suivante */}
        <select
          className="crm-opp-move-select"
          value={opp.etape_id}
          onChange={e => onMove(opp.id, e.target.value)}
          title="Changer l'étape"
        >
          {etapes.map(e => <option key={e.id} value={e.id}>{e.libelle}</option>)}
        </select>
        <button className="crm-card-btn" onClick={() => onEdit(opp)} title="Modifier">✎</button>
        <button className="crm-card-btn crm-card-btn--danger" onClick={() => onDelete(opp.id)} title="Supprimer">✕</button>
      </div>
    </div>
  );
}

// ── Colonne kanban ────────────────────────────────────────────
function KanbanColumn({ etape, opps, etapes, onMove, onEdit, onDelete }) {
  const total = opps.reduce((s, o) => s + (parseFloat(o.montant_ht) || 0), 0);
  return (
    <div className="crm-kanban-col">
      <div className="crm-kanban-col-header" style={{ borderTop: `3px solid ${etape.couleur || '#5c6078'}` }}>
        <span className="crm-kanban-col-title">{etape.libelle}</span>
        <span className="crm-kanban-col-count">{opps.length}</span>
      </div>
      {total > 0 && <div className="crm-kanban-col-total">{fmtEur(total)}</div>}
      <div className="crm-kanban-col-body">
        {opps.map(o => (
          <OppCard key={o.id} opp={o} etapes={etapes}
            onMove={onMove} onEdit={onEdit} onDelete={onDelete} />
        ))}
        {opps.length === 0 && <div className="crm-kanban-empty">Aucune</div>}
      </div>
    </div>
  );
}

export default function CRMOpportunites() {
  const [opps, setOpps]         = useState([]);
  const [etapes, setEtapes]     = useState([]);
  const [clients, setClients]   = useState([]);
  const [canaux, setCanaux]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [view, setView]         = useState('kanban'); // 'kanban' | 'list'
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [filter, setFilter]     = useState({ commercial_id: '', client_id: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ view: 'kanban', limit: 200 });
      if (filter.commercial_id) params.set('commercial_id', filter.commercial_id);
      if (filter.client_id)     params.set('client_id', filter.client_id);
      const [oppsR, etapesR] = await Promise.all([
        apiFetch(`/crm/opportunites?${params}`),
        apiFetch('/crm/pipeline/etapes'),
      ]);
      setOpps(oppsR.data || []);
      setEtapes(etapesR.data || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    apiFetch('/crm/clients?limit=500').then(r => setClients(r.data || [])).catch(() => {});
    apiFetch('/crm/canaux-source').then(r => setCanaux(r.data || [])).catch(() => {});
  }, []);

  async function handleMove(oppId, etapeId) {
    try {
      await apiFetch(`/crm/opportunites/${oppId}/etape`, {
        method: 'PATCH',
        body: JSON.stringify({ etape_id: parseInt(etapeId) }),
      });
      load();
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Supprimer cette opportunité ?')) return;
    try {
      await apiFetch(`/crm/opportunites/${id}`, { method: 'DELETE' });
      load();
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  // Grouper les opps par étape (kanban)
  const oppsByEtape = etapes.reduce((acc, e) => {
    acc[e.id] = opps.filter(o => String(o.etape_id) === String(e.id));
    return acc;
  }, {});

  const totalCA = opps.reduce((s, o) => s + (parseFloat(o.montant_ht) || 0), 0);
  const casPondere = opps.reduce((s, o) => s + (parseFloat(o.montant_ht) || 0) * (o.probabilite || 0) / 100, 0);

  return (
    <div className="crm-section-page crm-section-page--full">

      {/* Barre d'outils */}
      <div className="crm-toolbar">
        <div className="crm-toolbar-left">
          <button
            className={`crm-view-btn${view === 'kanban' ? ' active' : ''}`}
            onClick={() => setView('kanban')}>
            ⬛ Kanban
          </button>
          <button
            className={`crm-view-btn${view === 'list' ? ' active' : ''}`}
            onClick={() => setView('list')}>
            ☰ Liste
          </button>
        </div>
        <div className="crm-toolbar-right">
          {totalCA > 0 && (
            <span className="crm-toolbar-stat">
              Total : <strong>{fmtEur(totalCA)}</strong>
              <span className="crm-toolbar-stat-sub"> · Pondéré : {fmtEur(casPondere)}</span>
            </span>
          )}
          <button className="btn btn-primary crm-btn-sm"
            onClick={() => { setEditing(null); setShowModal(true); }}>
            + Opportunité
          </button>
        </div>
      </div>

      {loading && <Loading />}
      {error && <div className="crm-error">{error}</div>}

      {!loading && !error && view === 'kanban' && (
        <div className="crm-kanban-board">
          {etapes.map(e => (
            <KanbanColumn
              key={e.id}
              etape={e}
              opps={oppsByEtape[e.id] || []}
              etapes={etapes}
              onMove={handleMove}
              onEdit={o => { setEditing(o); setShowModal(true); }}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {!loading && !error && view === 'list' && (
        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead>
              <tr>
                <th>Intitulé</th>
                <th>Client</th>
                <th>Étape</th>
                <th>Montant HT</th>
                <th>Probabilité</th>
                <th>Closing prévu</th>
                <th>Commercial</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {opps.length === 0 && (
                <tr><td colSpan={8} className="crm-empty-row">Aucune opportunité</td></tr>
              )}
              {opps.map(o => (
                <tr key={o.id} className="crm-table-row">
                  <td><strong>{o.intitule}</strong></td>
                  <td>{o.nom_client}</td>
                  <td>
                    <span className="crm-badge"
                      style={{ background: (o.etape_couleur || '#5c6078') + '22', color: o.etape_couleur || '#b8bdd0' }}>
                      {o.etape_libelle}
                    </span>
                  </td>
                  <td>{o.montant_ht ? fmtEur(o.montant_ht) : '—'}</td>
                  <td>{o.probabilite}%</td>
                  <td style={{ color: o.date_closing_prevue && new Date(o.date_closing_prevue) < new Date() ? '#f87171' : 'inherit' }}>
                    {fmtDate(o.date_closing_prevue)}
                  </td>
                  <td className="crm-text-sm">{o.commercial_nom || '—'}</td>
                  <td>
                    <button className="crm-btn-edit" onClick={() => { setEditing(o); setShowModal(true); }}>✎</button>
                    <button className="crm-btn-edit crm-card-btn--danger" onClick={() => handleDelete(o.id)}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <ModalOpportunite
          opp={editing}
          clients={clients}
          etapes={etapes}
          canaux={canaux}
          onSave={() => { setShowModal(false); load(); }}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
